/**
 * Site-wide pull-to-refresh for mobile browsers.
 * Resolves the active scroll container per gesture via document-level touch delegation.
 */

import React, { useEffect, useRef, useState } from 'react';
import { PullToRefreshIndicator } from '@/components/ui/PullToRefreshIndicator';
import {
  findPullToRefreshRootFromTarget,
  isMobileBrowserPullToRefreshEnabled,
  isScrollRootAtTop,
  MAX_HORIZONTAL_DRIFT,
  PULL_THRESHOLD_PX,
} from '@/utils/pullToRefresh';

export const GlobalPullToRefresh: React.FC = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!isMobileBrowserPullToRefreshEnabled()) return;

    let startY = 0;
    let startX = 0;
    let armed = false;
    let activeRoot: HTMLElement | null = null;

    const reset = () => {
      armed = false;
      activeRoot = null;
      setIsPulling(false);
      setPullDistance(0);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;

      activeRoot = findPullToRefreshRootFromTarget(e.target);
      if (!activeRoot || !isScrollRootAtTop(activeRoot)) {
        armed = false;
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      startY = touch.clientY;
      startX = touch.clientX;
      armed = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!armed || isRefreshingRef.current || !activeRoot) return;

      if (!isScrollRootAtTop(activeRoot)) {
        reset();
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      const deltaY = touch.clientY - startY;
      const deltaX = Math.abs(touch.clientX - startX);

      if (deltaX > MAX_HORIZONTAL_DRIFT && deltaX > Math.abs(deltaY)) {
        reset();
        return;
      }

      if (deltaY > 0) {
        setIsPulling(true);
        setPullDistance(Math.min(deltaY, PULL_THRESHOLD_PX * 1.5));
      } else {
        reset();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!armed || isRefreshingRef.current || !activeRoot) {
        reset();
        return;
      }

      const touch = e.changedTouches[0];
      if (!touch) {
        reset();
        return;
      }

      const deltaY = touch.clientY - startY;
      const deltaX = Math.abs(touch.clientX - startX);

      if (
        isScrollRootAtTop(activeRoot) &&
        deltaY >= PULL_THRESHOLD_PX &&
        deltaX <= MAX_HORIZONTAL_DRIFT
      ) {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        window.location.reload();
        return;
      }

      reset();
    };

    const onTouchCancel = () => reset();

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };
  }, []);

  if (!isMobileBrowserPullToRefreshEnabled()) return null;

  return (
    <PullToRefreshIndicator
      pullDistance={pullDistance}
      isPulling={isPulling}
      isRefreshing={isRefreshing}
      fixed
    />
  );
};
